import { useLazyQuery } from '@apollo/client'
import { BookingGetHourlyPriceQuery, BookingGetHourlyPriceQueryVariables } from 'parkdepot-shared/gql/graphql'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { CheckoutContext } from '../../context/CheckoutContext/checkout.context'
import { LotContext } from '../../context/LotContext/lot.context'
import { GET_HOURLY_PRICE } from '../../graphql/queries/getHourlyPrice.query'
import { useGetUser } from '../../hooks/useAuth'
import { getLotPricingMemoized } from '../../utils/getLotPricing.utils'
import useParseOpeningHours from '../../utils/getOpeningHours.utils'
import HourlyBooking from './components/HourlyBooking/HourlyBooking.component'
import { lotRoute } from './route'
import { generateHourlyPriceQueryOptions } from './utils/generateHourlyPriceQueryOptions.utils'
import { getMinOrMaxFromISODuration } from '../../utils/getMinOrMaxFromISODuration.utils'
import memoize from '../../utils/memoize.utils'
import { LotDurationType, LotViewName } from '../../types/lot.interface'

const ShortDuration = () => {
    const [hourlyNoOfHours, setHourlyNoOfHours] = useState(1)
    const [isAfterOpeningStartHour, setIsAfterOpeningStartHour] = useState(true)
    const history = useHistory()
    const location = useLocation()
    const isMounted = useRef(false)

    const { user } = useGetUser()
    const { modifyAndSetHourlyCheckout } = useContext(CheckoutContext)
    const { lot, setNextButtonLoading, setNextDisabled } = useContext(LotContext)
    const { getDateOpeningHour, isAfterOpeningHour } = useParseOpeningHours()

    const minimumDuration = memoize(getMinOrMaxFromISODuration)(lot?.min_booking_duration || 'PT1H', 'min')

    const [getHourlyPrice] = useLazyQuery<BookingGetHourlyPriceQuery, BookingGetHourlyPriceQueryVariables>(
        GET_HOURLY_PRICE
    )

    const updateUrlWithHourlyNoOfHours = (value: number) => {
        const params = new URLSearchParams(location.search)
        params.set('duration', value.toString())

        history.replace({
            pathname: location.pathname,
            search: params.toString()
        })
    }

    const onHourlyCheckPrice = async (hours?: number) => {
        if (!lot) return
        setNextButtonLoading(true)
        const durationFromUrl = parseInt(new URLSearchParams(location.search).get('duration') || '1')
        // check for minimum duration
        const checkedDuration = durationFromUrl < minimumDuration ? minimumDuration : durationFromUrl
        const maxNumberOfHour = getDateOpeningHour(new Date(), lot?.openingHours)

        // oh boy, this is a mess
        const duration = checkedDuration > maxNumberOfHour ? maxNumberOfHour : checkedDuration > 0 ? checkedDuration : 1

        setHourlyNoOfHours(hours || duration)
        const value = hours || duration
        try {
            const pricingType = getLotPricingMemoized(user, lot.id)
            const { data: priceData } = await getHourlyPrice(
                generateHourlyPriceQueryOptions(lot.id, value, pricingType.pricing_type)
            )
            if (!isMounted.current) return

            const prices = priceData?.booking_get_price_hourly?.offered_prices ?? []

            // get last price from offered_prices
            if (lot && prices.length > 0) {
                const price = prices[prices.length - 1]
                modifyAndSetHourlyCheckout(lot, price || 0, value)
                updateUrlWithHourlyNoOfHours(value)
            }

            setNextDisabled(false)
        } finally {
            setNextButtonLoading(false)
        }
    }

    const onHourlyNoOfHoursChange = async (value: number) => {
        setHourlyNoOfHours(value)
        await onHourlyCheckPrice(value)
    }

    useEffect(() => {
        if (!lot) return

        // redirect to lot page if feature not enabled
        const hourlyFeature = lot.active_features.find((f) => f.name.toLowerCase() === LotViewName.HOURLY.toLowerCase())
        if (!hourlyFeature?.enabled) {
            history.push({ pathname: lotRoute(lot.id) })
            return
        }

        const afterOpeningStartHour = isAfterOpeningHour(lot.openingHours)
        setIsAfterOpeningStartHour(afterOpeningStartHour)
        if (!afterOpeningStartHour) {
            setNextDisabled(true)
            return
        }
        const maxNumberOfHour = getDateOpeningHour(new Date(), lot?.openingHours)
        if (maxNumberOfHour === 0) {
            history.push({ pathname: lotRoute(lot.id) })
            return
        }
        if (maxNumberOfHour > 0) {
            onHourlyCheckPrice()
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, lot, getDateOpeningHour])

    useEffect(() => {
        const currentParams = new URLSearchParams(location.search)

        let updated = false

        if (!currentParams.has('feature')) {
            currentParams.set('feature', LotDurationType.SHORT)
            updated = true
        }

        if (!currentParams.has('duration')) {
            currentParams.set('duration', hourlyNoOfHours.toString())
            updated = true
        }

        if (updated) {
            history.replace({
                pathname: location.pathname,
                search: currentParams.toString()
            })
        }
    }, [history, location.pathname, location.search, hourlyNoOfHours])

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    if (!lot) return null

    return (
        <HourlyBooking
            isAfterOpeningStartHour={isAfterOpeningStartHour}
            selectedHour={hourlyNoOfHours}
            openingHours={lot.openingHours}
            minimumDuration={minimumDuration}
            onChange={onHourlyNoOfHoursChange}
        />
    )
}

export default ShortDuration
